<template>
  <section class="flex flex-col">
    <button class="flex h-12 items-center justify-between" :aria-expanded="accordionOpen" @click="accordionOpen = !accordionOpen">
      <div class="flex gap-2">
        <h2 class="text-type-primary font-medium">{{ sectionName }}</h2>
        <div class="bg-primary-200 text-2xs mt-[5px] flex size-4 items-center justify-center rounded-full font-medium text-white">
          {{ hazardLength }}
          <p class="sr-only">item{{ hazardLength != 1 ? 's' : '' }}</p>
        </div>
      </div>
      <gv-icon
        name="expand_more"
        class="cursor-pointer select-none transition-transform"
        :class="{
          'rotate-180': accordionOpen,
        }"
        aria-hidden="true"
      />
    </button>
    <transition>
      <div
        v-show="accordionOpen"
        ref="accordion"
        :aria-hidden="!accordionOpen"
        class="flex flex-col gap-2 overflow-hidden transition-all ease-in-out"
      >
        <template v-if="fireDangerRatings">
          <ui-list-tile
            v-for="hazard in fireDangerRatingsByDate"
            :id="hazard.id"
            :key="hazard.id"
            :description="hazard.forecast?.fireDanger"
            :title="hazard.title"
            :background-color="hazard.scheme?.backgroundColor"
            :published-at-text="hazards.dates[hazards.selectedDateIndex]"
            :leading-color="hazard.scheme?.leadingColor"
            :icon="hazard.scheme?.icon"
            :selected="[selectedHazard?.hazardId, selectedPopupHazard?.hazardId].includes(hazard.id)"
            @click="onClick(hazard)"
          />
        </template>

        <template v-else>
          <ui-list-tile
            v-for="hazard in filteredHazards"
            :id="hazard.id"
            :key="hazard.id"
            :description="hazard.description"
            :title="hazard.title"
            :background-color="hazard.backgroundColor"
            :published-at="hazard.publishedAt"
            :relative-date-time-format="getRelativeDateTimeFormat(hazard)"
            :leading-color="hazard.leadingColor"
            :icon="hazard.icon"
            :selected="[selectedHazard?.hazardId, selectedPopupHazard?.hazardId].includes(hazard.id)"
            @click="onClick(hazard)"
          />
        </template>

        <template v-if="isWarningsSection">
          <ui-list-tile
            v-for="hazard in additionalWarnings"
            :id="hazard.id"
            :key="hazard.id"
            :description="hazard.description"
            :title="hazard.title"
            :background-color="hazard.backgroundColor"
            :published-at="hazard.publishedAt"
            :published-at-text="hazard.entityType == HazardType.FireDangerRating ? hazards.dates[hazards.selectedDateIndex] : undefined"
            :leading-color="hazard.leadingColor"
            :icon="hazard.icon"
            :selected="[selectedHazard?.hazardId, selectedPopupHazard?.hazardId].includes(hazard.id)"
            @click="onClickAdditionalWarning(hazard)"
          />
        </template>
      </div>
    </transition>
  </section>
</template>

<script setup lang="ts">
import {
  HazardEntity,
  UiListSectionType,
  hazardTypeView,
  useHazardsStore,
  useMapStore,
  HazardType,
  MapMode,
  EntitySubType,
  HazardPriorityMapping,
  TotalFireBanEntity,
  RelativeDateTimeFormat,
} from '@/features/hazards';
import { useResizeObserver } from '@vueuse/core';
import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

const props = defineProps<{
  sectionType: UiListSectionType;
  filteredHazards: HazardEntity[];
  fireDangerRatings: boolean;
}>();

const accordionOpen = ref(true);
const accordion = ref<HTMLElement | null>(null);

const hazards = useHazardsStore();
const { selectedHazard, totalFireBanForToday, extremeOrCatastrophicFireDangerRatingForToday, isSomeTypeFilterActive, typesFilters } =
  storeToRefs(hazards);

const map = useMapStore();
const { selectedPopupHazard } = storeToRefs(map);

const isFireBansSection = computed<boolean>(() => props.sectionType === UiListSectionType.TotalFireBans);
const isFireDangerRatingSection = computed<boolean>(() => props.sectionType === UiListSectionType.FireDangerRatings);
const isWarningsSection = computed<boolean>(() => props.sectionType === UiListSectionType.Warnings);

/**
 * Additional warnings are warnings that are not part of the main list of hazards.
 * For example, Total Fire Bans and Extreme or Catastrophic Fire Danger Ratings.
 *
 * These also have a bespoke onClick handler.
 */
const additionalWarnings = computed<HazardEntity[]>(() => {
  if (isSomeTypeFilterActive.value && !Object.values(typesFilters.value.Warnings).every((value) => value)) return [];

  const warningHazards: HazardEntity[] = [];
  if (totalFireBanForToday.value) {
    warningHazards.push(totalFireBanForToday.value as HazardEntity);
  }
  if (extremeOrCatastrophicFireDangerRatingForToday.value) {
    warningHazards.push(extremeOrCatastrophicFireDangerRatingForToday.value as HazardEntity);
  }
  return warningHazards;
});

const onClickAdditionalWarning = (hazard: HazardEntity) => {
  if (hazard.entityType === HazardType.TotalFireBans) {
    hazards.setMapMode(MapMode.FireBan);
  } else if (hazard.entityType === HazardType.FireDangerRating) {
    hazards.setMapMode(MapMode.FireDanger);
  }
};

/**
 * Fire danger ratings need to be sorted by their rating level
 */
const fireDangerRatingsByDate = computed(() => {
  if (!props.fireDangerRatings) {
    return [];
  }

  return props.filteredHazards
    .map((fh) => {
      return {
        ...fh,
        scheme: fh.pointFeature?.properties?.schemes?.[hazards.selectedDateIndex],
        forecast: fh.pointFeature?.properties?.forecasts?.[hazards.selectedDateIndex],
      };
    })
    .sort((a, b) => {
      const bPriority = HazardPriorityMapping[b.scheme.entitySubType as (typeof EntitySubType)[keyof typeof EntitySubType]] ?? 0;
      const aPriority = HazardPriorityMapping[a.scheme.entitySubType as (typeof EntitySubType)[keyof typeof EntitySubType]] ?? 0;

      return bPriority - aPriority;
    });
});

const sectionName = computed(() => {
  if (!(isFireDangerRatingSection.value || isFireBansSection.value)) {
    return props.sectionType;
  }

  if (hazards.selectedDateIndex === 0) return `${props.sectionType} TODAY`;
  if (hazards.selectedDateIndex === 1) return `${props.sectionType} TOMORROW`;

  const today = DateTime.now();

  const in2Days = today.plus({ days: 2 });
  const in3Days = today.plus({ days: 3 });

  return hazards.selectedDateIndex === 2
    ? `${props.sectionType} ${in2Days.toFormat('dd MMM')}`
    : `${props.sectionType} ${in3Days.toFormat('dd MMM')}`;
});

const onClick = (hazard: HazardEntity) => {
  hazards.toggleSelectHazard({
    hazardType: hazard.entityType,
    hazardId: hazard.id,
    eventId: hazard.eventId!,
    viewType: hazardTypeView(hazard.entityType),
  });
};

const hazardLength = computed(() => {
  if (isFireBansSection.value) {
    return props.filteredHazards.filter((filteredHazard) => (filteredHazard as TotalFireBanEntity).banLevel === 'Ban').length;
  }
  return props.filteredHazards.length;
});

const setAccordionMaxHeight = () => {
  if (!accordion.value) {
    return;
  }

  accordion.value.style.maxHeight = accordionOpen.value ? `${accordion.value.scrollHeight}px` : '0px';
};

/**
 * Get the relative date format for the hazard.
 *
 * This is used to determine how the date is displayed in the list.
 * e.g. for the DateTime.now().plus({ days: 1 }) date:
 * `RelativeDateTimeFormat.calendar` would display "Tomorrow"
 * `RelativeDateTimeFormat.default` would display "in 1 day"
 * @param hazard
 */
const getRelativeDateTimeFormat = (hazard: HazardEntity): RelativeDateTimeFormat => {
  if (hazard.entityType === HazardType.PrescribedBurns) {
    return RelativeDateTimeFormat.calendar;
  }

  return RelativeDateTimeFormat.default;
};

watch(
  [() => props.filteredHazards, accordionOpen],
  () => {
    if (!props.filteredHazards.length && !additionalWarnings.value.length) {
      return;
    }
    nextTick(() => {
      setAccordionMaxHeight();
    });
  },
  { immediate: true }
);

const resizeObserver = useResizeObserver(accordion, () => {
  setAccordionMaxHeight();
});

onMounted(() => {
  window.addEventListener('load', () => {
    setAccordionMaxHeight();
  });
});

onUnmounted(() => {
  resizeObserver.stop();
});
</script>
